/**
 * GroupMemberItem: un partecipante del gruppo con ruolo, nome nel CRM e (per gli
 * amministratori) le azioni nomina/togli amministratore e rimuovi.
 */

import { memo } from 'react';
import { Crown, Shield, ShieldOff, ShieldPlus, UserMinus, Loader2 } from 'lucide-react';
import type { GroupRole } from '@/lib/services/whatsapp/wahaGroups';
import { iconTip } from '@/components/messages/ChatTooltip';

export interface GroupParticipantView {
  id: string;
  role: GroupRole;
  /** Nome principale: Lead/Cliente nel CRM, poi nome WhatsApp, poi numero. */
  displayName: string;
  /** Numero leggibile, se noto. */
  phoneLabel: string | null;
  inCrm: boolean;
  isMe: boolean;
}

interface GroupMemberItemProps {
  participant: GroupParticipantView;
  color: string;
  /** Mostra le azioni (il nostro numero e' amministratore o non verificabile). */
  canManage: boolean;
  busy?: boolean;
  onRemove?: (p: GroupParticipantView) => void;
  onToggleAdmin?: (p: GroupParticipantView) => void;
}

const btn = 'p-1.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed';

export const GroupMemberItem = memo(function GroupMemberItem({
  participant: p,
  color,
  canManage,
  busy,
  onRemove,
  onToggleAdmin,
}: GroupMemberItemProps) {
  const isSuperAdmin = p.role === 'superadmin';
  const isAdmin = p.role === 'admin';
  const showActions = canManage && !p.isMe && !isSuperAdmin;

  return (
    <div className="group/member flex items-center gap-3 px-4 py-2.5 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      >
        {(p.displayName.replace(/^\+/, '').charAt(0) || '?').toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-[#37352F] dark:text-white truncate">
          {p.isMe ? 'Tu (questo numero)' : p.displayName}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5 text-xs text-[#787774] min-w-0">
          {isSuperAdmin && <><Crown className="w-3 h-3 text-amber-500 flex-shrink-0" /><span className="text-amber-600">Creatore</span></>}
          {isAdmin && <><Shield className="w-3 h-3 text-[#2383E2] flex-shrink-0" /><span className="text-[#2383E2]">Amministratore</span></>}
          {p.inCrm && !p.isMe && <span className="rounded bg-emerald-50 dark:bg-emerald-900/30 px-1 text-emerald-700 dark:text-emerald-300">Nel CRM</span>}
          {p.phoneLabel && p.phoneLabel !== p.displayName && <span className="truncate">{p.phoneLabel}</span>}
        </div>
      </div>

      {showActions && (
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin text-[#787774]" aria-label="Operazione in corso" />
          ) : (
            <>
              {onToggleAdmin && (
                <button type="button" className={btn} onClick={() => onToggleAdmin(p)}
                  {...iconTip(isAdmin ? 'Togli amministratore' : 'Nomina amministratore', 'left')}>
                  {isAdmin
                    ? <ShieldOff className="w-4 h-4 text-[#787774]" />
                    : <ShieldPlus className="w-4 h-4 text-[#787774]" />}
                </button>
              )}
              {onRemove && (
                <button type="button" className={btn} onClick={() => onRemove(p)} {...iconTip('Rimuovi dal gruppo', 'left')}>
                  <UserMinus className="w-4 h-4 text-red-500" />
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});
